/**
 * Ai Hire data access. Preview: every function resolves sample data from `aiHireMockData.js`.
 * Later each body becomes an API call with the same name and return shape, and the tabs stay as they are.
 */
import {
  APPLICATIONS,
  CANDIDATES,
  FINALISTS,
  INTERVIEWERS,
  INTERVIEWS,
  JOBS,
  OFFERS,
  TODAY,
  WEEK_DAYS,
} from "./aiHireMockData";
import { reached, recommendationFor } from "./aiHireStages";

const byId = (list) => Object.fromEntries(list.map((x) => [x.id, x]));
const JOB_BY_ID = byId(JOBS);
const CANDIDATE_BY_ID = byId(CANDIDATES);
const INTERVIEWER_BY_ID = byId(INTERVIEWERS);
const APPLICATION_BY_ID = byId(APPLICATIONS);

const clampScore = (n) => Math.max(0, Math.min(100, Math.round(n)));
const SKILL_SPREAD = [6, -4, 2, -8];

/** What Ai Hire's interview report will look like (score per job skill, strengths, concerns, highlights). */
function aiInterviewReport(app, job) {
  if (app.aiInterviewScore == null) return null;
  const skills = job.skills
    .map((name, i) => ({ name, score: clampScore(app.aiInterviewScore + SKILL_SPREAD[i % SKILL_SPREAD.length]) }))
    .sort((a, b) => b.score - a.score);
  const weakest = skills[skills.length - 1];
  return {
    score: app.aiInterviewScore,
    recommendation: recommendationFor(app.aiInterviewScore),
    durationMinutes: 24,
    skills,
    strengths: skills.slice(0, 2).map((s) => `Clear, confident answers on ${s.name}`),
    concerns: [`Needs more depth in ${weakest.name}`],
    highlights: [
      {
        q: `Walk us through a recent ${job.skills[0]} task you owned end to end.`,
        a: "Explained the problem, the trade-offs considered and how the result was measured.",
      },
      {
        q: `How would you handle a tight deadline on the ${job.project} project?`,
        a: "Would agree the must-haves with the manager first, then ship in small, reviewed steps.",
      },
    ],
  };
}

function hydrate(app) {
  const job = JOB_BY_ID[app.jobId];
  return {
    ...app,
    job,
    candidate: CANDIDATE_BY_ID[app.candidateId],
    aiInterview: aiInterviewReport(app, job),
  };
}

const WEEK_INDEX_TODAY = WEEK_DAYS.findIndex((d) => d.date === TODAY);

export async function getSummary() {
  return {
    openJobs: JOBS.filter((j) => j.status === "Open").length,
    applicants: APPLICATIONS.length,
    aiShortlisted: APPLICATIONS.filter((a) => reached(a.stage, "shortlisted") || a.stage === "offer_declined").length,
    interviewsThisWeek: INTERVIEWS.length,
  };
}

/** Jobs with pipeline counts (applied, shortlisted, interview, offer). */
export async function listJobs() {
  return JOBS.map((job) => {
    const apps = APPLICATIONS.filter((a) => a.jobId === job.id);
    const madeIt = (id) => apps.filter((a) => reached(a.stage, id) || a.stage === "offer_declined").length;
    return {
      ...job,
      pipeline: {
        applied: apps.length,
        shortlisted: madeIt("shortlisted"),
        interview: madeIt("interview_scheduled"),
        offer: madeIt("offer_released"),
      },
    };
  });
}

/** Applications joined with candidate, job and AI interview report. */
export async function listApplications({ stages, jobId } = {}) {
  return APPLICATIONS.filter((a) => (!stages || stages.includes(a.stage)) && (!jobId || a.jobId === jobId)).map(
    hydrate
  );
}

export async function listInterviewers() {
  return INTERVIEWERS;
}

/** Interviews for the preview week, joined with application and interviewer. */
export async function listInterviews() {
  return INTERVIEWS.map((v) => {
    const interviewer = INTERVIEWER_BY_ID[v.interviewerId];
    return {
      ...v,
      application: hydrate(APPLICATION_BY_ID[v.applicationId]),
      interviewer,
      dayLabel: WEEK_DAYS[v.day].label,
      place: v.mode === "Online" ? "Online" : `Onsite - ${interviewer.location}`,
    };
  });
}

const isBusy = (interviewerId, day, hour) =>
  INTERVIEWS.some((v) => v.interviewerId === interviewerId && v.day === day && hour >= v.start && hour < v.end);

/**
 * Slot matcher (Ai Hire hook 3): one-hour slots later this week where the candidate's preferred
 * location and time window overlap a free interviewer at that location (or online).
 */
export async function suggestSlots(applicationId, limit = 3) {
  const { candidate } = hydrate(APPLICATION_BY_ID[applicationId]);
  const { preferredLocations, availability } = candidate;
  const slots = [];
  INTERVIEWERS.forEach((interviewer) => {
    if (!preferredLocations.includes(interviewer.location)) return;
    interviewer.days.forEach((day) => {
      if (day <= WEEK_INDEX_TODAY || !availability.days.includes(day)) return;
      const from = Math.max(interviewer.from, availability.from);
      const to = Math.min(interviewer.to, availability.to);
      for (let hour = from; hour < to; hour += 1) {
        if (isBusy(interviewer.id, day, hour)) continue;
        slots.push({
          day,
          dayLabel: WEEK_DAYS[day].label,
          start: hour,
          end: hour + 1,
          interviewer,
          place: interviewer.location === "Online" ? "Online" : `Onsite - ${interviewer.location}`,
          rank: preferredLocations.indexOf(interviewer.location),
          tags: ["Location match", "Time match"],
        });
        break;
      }
    });
  });
  slots.sort((a, b) => a.rank - b.rank || a.day - b.day || a.start - b.start);
  const oneEachDay = [];
  slots.forEach((s) => {
    if (oneEachDay.length < limit && !oneEachDay.some((x) => x.day === s.day)) oneEachDay.push(s);
  });
  return oneEachDay;
}

/** Finalized applications with scorecards, CTC and approval. */
export async function listFinalists() {
  return APPLICATIONS.filter((a) => a.stage === "finalized").map((a) => ({ ...hydrate(a), ...FINALISTS[a.id] }));
}

export async function listOffers() {
  return OFFERS.map((o) => ({ ...o, application: hydrate(APPLICATION_BY_ID[o.applicationId]) }));
}
